import { type Money } from "./application";

export enum PropertyKind {
  MultiFamily = "multi_family",
  SingleFamily = "single_family",
  ManufacturedHousing = "manufactured_housing"
}

export const propertyKindLabel: Record<PropertyKind, string> = {
  [PropertyKind.SingleFamily]: "Single family",
  [PropertyKind.MultiFamily]: "Multi family",
  [PropertyKind.ManufacturedHousing]: "Manufactured housing"
};

export enum MediaAttachmentResolution {
  Full = "full",
  Large = "large",
  Medium = "medium",
  Small = "small"
}
interface MediaAttachment {
  global_id: string;
  url: string;
  content_type: string | null;
  original_filename: string | null;
  checksum: string | null;
  extension: string;
  size: number;
  resolution: MediaAttachmentResolution;
}

export interface MediaItem {
  id: number;
  position: number;
  is_cover: boolean;
  created_at: string;
  updated_at: string | null;
  attachments?: MediaAttachment[];
}

export interface ListingPlan {
  beds: number;
  sqft: number;
  baths: number;
}

export interface ImageVariantUrl {
  full: string;
  large?: string;
  medium?: string;
  small: string;
}

interface Amenity {
  key: string;
  level: "property" | "unit";
  title: string;
}

export type ListingPageFilterKey =
  | "price"
  | "beds"
  | "baths"
  | "pets"
  | "listing_type"
  | "state"
  | "city"
  | "move_in_date"
  | "specific_properties"
  | "map";

export type ListingPageDefaultSort =
  | "default"
  | "price_asc"
  | "price_desc"
  | "distance";

export type ListingPageCardFieldKey =
  | "property_name"
  | "price"
  | "beds_baths_sqft"
  | "pets_badge"
  | "photo_carousel";

export interface ListingPageFilterSetting {
  key: ListingPageFilterKey;
  enabled: boolean;
}

export interface ListingPageConfig {
  filters: ListingPageFilterSetting[];
  default_sort: ListingPageDefaultSort;
  pinned_property_ids: number[];
  card_fields: Record<ListingPageCardFieldKey, boolean>;
}

export const LISTING_PAGE_FILTER_ORDER: ListingPageFilterKey[] = [
  "listing_type",
  "specific_properties",
  "price",
  "city",
  "state",
  "beds",
  "baths",
  "pets",
  "move_in_date",
  "map"
];

export const LISTING_PAGE_CARD_FIELD_KEYS: ListingPageCardFieldKey[] = [
  "property_name",
  "price",
  "beds_baths_sqft",
  "pets_badge",
  "photo_carousel"
];

export const LISTING_PAGE_MAX_PINNED_PROPERTIES = 50;

export const DEFAULT_LISTING_PAGE_CONFIG: ListingPageConfig = {
  filters: LISTING_PAGE_FILTER_ORDER.map((key) => ({ key, enabled: true })),
  default_sort: "default",
  pinned_property_ids: [],
  card_fields: {
    property_name: true,
    price: true,
    beds_baths_sqft: true,
    pets_badge: true,
    photo_carousel: true
  }
};

const DEFAULT_SORT_VALUES: ListingPageDefaultSort[] = [
  "default",
  "price_asc",
  "price_desc",
  "distance"
];

export const normalizeListingPageConfig = (
  raw?: Partial<ListingPageConfig> | null
): ListingPageConfig => {
  if (!raw) return DEFAULT_LISTING_PAGE_CONFIG;

  const rawFilters = Array.isArray(raw.filters) ? raw.filters : [];
  const seen = new Set<ListingPageFilterKey>();
  const filters: ListingPageFilterSetting[] = [];
  rawFilters.forEach((filter) => {
    const key = filter?.key;
    if (LISTING_PAGE_FILTER_ORDER.includes(key) && !seen.has(key)) {
      seen.add(key);
      filters.push({ key, enabled: Boolean(filter.enabled) });
    }
  });
  LISTING_PAGE_FILTER_ORDER.forEach((key) => {
    if (!seen.has(key)) filters.push({ key, enabled: true });
  });

  const defaultSort = DEFAULT_SORT_VALUES.includes(
    raw.default_sort as ListingPageDefaultSort
  )
    ? (raw.default_sort as ListingPageDefaultSort)
    : "default";

  const rawCardFields: Partial<Record<ListingPageCardFieldKey, boolean>> =
    raw.card_fields ?? {};
  const cardFields = {} as Record<ListingPageCardFieldKey, boolean>;
  LISTING_PAGE_CARD_FIELD_KEYS.forEach((key) => {
    const value = rawCardFields[key];
    cardFields[key] = value === undefined ? true : Boolean(value);
  });

  const pinnedPropertyIds = Array.isArray(raw.pinned_property_ids)
    ? raw.pinned_property_ids.filter(
        (id): id is number => typeof id === "number"
      )
    : [];

  return {
    filters,
    default_sort: defaultSort,
    pinned_property_ids: pinnedPropertyIds,
    card_fields: cardFields
  };
};

export interface PartnerDetails {
  apply_token: string;
  amenities: {
    property: Amenity[];
    unit: Amenity[];
  };
  name: string;
  slug: string;
  url: string;
  asset_logo_url: string | null;
  colors?: {
    primary: Record<string, string>;
    background?: Record<string, string>;
  };
  phone_number: string | null;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone_number: string;
  email: string;
  is_english_only?: boolean;
  home_site_rent?: boolean;
  sites_and_communities?: boolean;
  property_type?: PropertyKind | null;
  // Partner's own GA4 Measurement ID(s) (Portal → Settings → Integration →
  // Google Analytics). Free-text setting; the portal stores an array, but a
  // legacy single string is also possible. Absent/empty means GA4 is disabled.
  google_analytics_id?: string | string[] | null;
  listing_page_config?: ListingPageConfig;
  // Google Ads conversion tracking (Portal → Settings → Integration → Google
  // Ads). Conversion ID (e.g. "AW-123456789") plus one label per conversion
  // goal. Absent/empty means Ads conversion reporting is disabled (CRM-952).
  google_ads_conversion_id?: string | null;
  google_ads_contact_form_conversion_label?: string | null;
  google_ads_showing_conversion_label?: string | null;
}

export interface PropertyDetails {
  name: string;
  slug: string;
  primary_image_url: ImageVariantUrl | null;
  kind: PropertyKind;
  address: {
    address1: string;
    one_line_address: string;
    city: string;
    state_or_province: string;
    postal_code: string;
    country: string;
  };
  branding: {
    enabled?: boolean;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    logo?: {
      value: string;
      asset: {
        url: string;
        content_type: string;
        filename: string;
        size: number;
        extension: string;
      };
    };
    palette?: Record<string, string>;
  };
  custom_button_url: string | null;
}

export type RentDisplayMode = "both" | "home" | "site" | "none";

export type ListingPricePrimaryDisplay = "sale_price" | "home_rent" | "hide";
export type ListingPriceSecondaryDisplay = "site_rent" | "hide";

export interface MapPin {
  slug: string;
  lat: number;
  lng: number;
  unit_url: string;
  title: string;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  rent_display_mode: RentDisplayMode;
  listing_price_primary: Money | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary: Money | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  sale_price: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  occupied: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  property_name: string;
  property_address: string;
}

export type SecurityDepositKind = "first_month_rent" | "amount";

export interface ListingAdditionalFees {
  application_fee: {
    enabled: boolean;
    amount: Money | null;
  };
  security_deposit: {
    enabled: boolean;
    kind: SecurityDepositKind | null;
    amount: Money | null;
  };
  other_fees: {
    enabled: boolean;
    text: string | null;
  };
}

interface BaseListing {
  id: number;
  slug: string;
  partner_slug: string;
  title: string;
  description: string | null;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  rent_display_mode: RentDisplayMode;
  listing_price_primary: Money | null;
  listing_price_secondary: Money | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  sale_price: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  available_at: string | null;
  occupied: boolean;
  waitlist: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  city: string;
  state_or_province: string;
  postal_code: string;
  unit_url: string;
  lat: number | null;
  lng: number | null;
  media: MediaItem[];
  property: PropertyDetails;
  amenities: {
    id: number;
    title: string;
  }[];
  show_apply_now_button: boolean;
  custom_button_url: string | null;
  additional_fees: ListingAdditionalFees | null;
  // Unit-sourced fees (AppFolio), integer minor units (cents), nullable. When
  // present these take precedence over the Listing-template `additional_fees`
  // toggles on the public listing page.
  application_fee_cents: number | null;
  security_deposit_cents: number | null;
}

interface WithML {
  can_apply: true;
  magic_link: {
    id: string;
    monthly_rent_amount: Money;
    created_at: string;
    updated_at: string;
    link: string;
    active: boolean;
    name: string | null;
    email: string | null;
    unit_id: string;
    property_id: string;
    application_id: string | null;
    kind: "unit";
  };
}

interface WithoutML {
  can_apply: false;
}

export type Listing = BaseListing & (WithML | WithoutML);

export enum QuestionType {
  YesNo = "yes_no",
  MultipleChoice = "multiple_choice",
  PlainText = "plain_text"
}

export interface PrescreeningQuestion {
  id: string;
  type: QuestionType;
  name: string;
  question: string;
  required: boolean;
  order: number;
  // Stable value to submit back — always English, never localized.
  options?: string[];
  // Display text for `options`, positionally matched and already resolved to
  // the current locale server-side. Display-only, never submit from this.
  option_labels?: string[];
  // Conditional (follow-up) question linkage. When both fields are populated
  // this entry is a conditional child: it is only shown to applicants once its
  // parent question is answered with `parent_answer`. Both are absent for
  // top-level questions. Snake_case matches the wire format produced/accepted
  // by boompay-api (the API traffic is not camelized in either client app).
  parent_question_id?: string;
  parent_answer?: string;
}

export enum PrescreeningMode {
  Range = "ranges",
  Numeric = "numeric"
}
export interface ListingButtons {
  show_apply_now_button: boolean;
  show_request_showing_button: boolean;
  show_contact_us_button: boolean;
  show_custom_button: boolean;
}
export interface PrescreeningQuestionRecord {
  id: number;
  prescreening_template_identifier: string;
  created_at: string;
  updated_at: string;
}
export type PrescreeningCreditScore = PrescreeningQuestionRecord & {
  score: number | null;
  score_range: string | null;
  score_unspecified?: boolean | null;
};
export type PrescreeningGrossMonthlyIncome = PrescreeningQuestionRecord & {
  income_cents: number | null;
  income_range: string | null;
};
